import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NetworkStatus } from '@apollo/client';

import { ToastTypes, GlobalToastTypeNames } from 'types';

import {
  useRunPandaDocSyncMutation,
  useRunPandaDocReparseMutation,
  usePandaDocSyncStatusQuery,
} from 'core/graphql';

import { useToasts } from 'core/hooks';

/** Status poll cadence while a sync is running (ТЗ §3.3: ~15-30 s). */
const POLL_INTERVAL_MS = 20000;
/** Soft cap — past this we stop polling and ask the user to reload (ТЗ §3.3). */
const SYNC_TIMEOUT_MS = 30 * 60 * 1000;

interface IPandadocSyncHook {
  /** Queues a full PandaDoc re-sync and switches the UI to progress mode. */
  triggerSync: () => void;
  /**
   * Queues the repair pass: re-parse signer contact data on the whole cached
   * inventory with the current parser, then drain whatever needs fresh
   * details. Admin-only on the backend. Shares this hook's progress polling.
   */
  triggerReparse: () => void;
  /** True while a sync/reparse is queued/running and progress is polled. */
  syncing: boolean;
  /** True when the run in progress is the reparse (for the button label). */
  reparsing: boolean;
  /** Documents still awaiting details; `null` until the first poll lands. */
  detailsPending: number | null;
}

/**
 * Orchestrates the manual «Обновить данные» button: fires `triggerPandadocSync`,
 * shows the backend toast message, then polls `pandadocSyncStatus` until the
 * details backlog drains (`detailsPending === 0`), a sync error appears, or the
 * soft timeout is hit. `onSynced` runs once on successful completion so the
 * caller can re-query its data (the provisioning folders list).
 */
export const usePandadocSync = (onSynced: () => void): IPandadocSyncHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const [syncing, setSyncing] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [detailsPending, setDetailsPending] = useState<number | null>(null);

  const startedAtRef = useRef(0);
  // Status responses seen since the trigger — the first one is the snapshot
  // taken right after enqueue and may still read 0 before the backend has
  // marked documents for backfill, so completion is judged from the second on.
  const responsesRef = useRef(0);
  // `lastError` as it stood BEFORE this run was queued — the trigger mutation
  // returns the pre-enqueue snapshot. Anything identical to it describes the
  // PREVIOUS run, not ours: the backend clears `last_error` when a run starts,
  // but that happens at worker pickup, which lags the enqueue by however long
  // the Celery queue is. Without this baseline the leftover error aborted the
  // progress UI on the very first poll and the sync looked unstartable.
  const baselineErrorRef = useRef('');
  // Flips once the backend proves our run is under way — it either holds the
  // run lock (`syncRunning`) or has cleared the previous error. Only then can
  // an observed error or a drained backlog be attributed to this run.
  const runStartedRef = useRef(false);
  const prevNetworkStatusRef = useRef<NetworkStatus>();
  // Kept in a ref so the polling effect needn't list `onSynced` as a dep.
  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;

  const [runSync] = useRunPandaDocSyncMutation();
  const [runReparse] = useRunPandaDocReparseMutation();

  const { data, networkStatus } = usePandaDocSyncStatusQuery({
    skip: !syncing,
    pollInterval: syncing ? POLL_INTERVAL_MS : 0,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const notify = useCallback(
    (type: ToastTypes, title: string) => {
      addToast({
        data: { type, title },
        type: GlobalToastTypeNames.PandadocSync,
      });
    },
    [addToast],
  );

  const stopPolling = useCallback(() => {
    setSyncing(false);
    setReparsing(false);
    setDetailsPending(null);
    responsesRef.current = 0;
    baselineErrorRef.current = '';
    runStartedRef.current = false;
    prevNetworkStatusRef.current = undefined;
  }, []);

  const triggerSync = useCallback(() => {
    if (syncing) {
      return;
    }
    startedAtRef.current = Date.now();
    responsesRef.current = 0;
    prevNetworkStatusRef.current = undefined;

    runSync()
      .then((result) => {
        const payload = result.data?.triggerPandadocSync;
        baselineErrorRef.current = payload?.syncStatus?.lastError ?? '';
        runStartedRef.current = false;
        if (payload?.message) {
          notify(ToastTypes.info, payload.message);
        }
        // Enter progress mode regardless of `enqueued`: the sync was either
        // just queued or is already running — both need polling (ТЗ §3.2).
        setSyncing(true);
      })
      .catch((error: Error) => {
        notify(
          ToastTypes.error,
          t('pages.pandadocProvisioning.toasts.syncError', {
            error: error.message,
          }),
        );
      });
  }, [syncing, runSync, notify, t]);

  const triggerReparse = useCallback(() => {
    if (syncing) {
      return;
    }
    startedAtRef.current = Date.now();
    responsesRef.current = 0;
    prevNetworkStatusRef.current = undefined;

    runReparse({ variables: { markRefetch: true } })
      .then((result) => {
        const payload = result.data?.reparsePandadocRecipients;
        baselineErrorRef.current = payload?.syncStatus?.lastError ?? '';
        runStartedRef.current = false;
        if (payload?.message) {
          notify(ToastTypes.info, payload.message);
        }
        // `enqueued: false` means a run already holds the lock — poll either
        // way, both cases end with the backlog drained (mirrors triggerSync).
        setSyncing(true);
        setReparsing(true);
      })
      .catch((error: Error) => {
        notify(
          ToastTypes.error,
          t('pages.pandadocProvisioning.toasts.reparseError', {
            error: error.message,
          }),
        );
      });
  }, [syncing, runReparse, notify, t]);

  useEffect(() => {
    if (!syncing) {
      return;
    }

    // Act once per completed fetch — only on the transition into `ready`.
    const justReady =
      networkStatus === NetworkStatus.ready &&
      prevNetworkStatusRef.current !== NetworkStatus.ready;
    prevNetworkStatusRef.current = networkStatus;
    if (!justReady) {
      return;
    }

    const status = data?.pandadocSyncStatus;
    if (!status) {
      return;
    }
    responsesRef.current += 1;

    if (Date.now() - startedAtRef.current > SYNC_TIMEOUT_MS) {
      notify(
        ToastTypes.warning,
        t('pages.pandadocProvisioning.toasts.syncTimeout'),
      );
      stopPolling();
      return;
    }

    // Proof that our run is under way: the backend holds the run lock, or it
    // has cleared whatever error the previous run left. Both happen only once
    // a worker has actually picked the task up. When nothing went wrong before
    // this run the baseline is empty, so this flips on the first poll and the
    // behaviour below is exactly what it always was.
    if (status.syncRunning || !status.lastError) {
      runStartedRef.current = true;
    }

    const pending = status.detailsPending ?? 0;
    setDetailsPending(pending);

    // Attribute an error to this run only once it has demonstrably started, or
    // when the message itself is new. A run still holding the lock is in
    // flight — its state says nothing final yet.
    const errorIsOurs =
      !!status.lastError &&
      !status.syncRunning &&
      (runStartedRef.current || status.lastError !== baselineErrorRef.current);

    if (errorIsOurs) {
      notify(
        ToastTypes.error,
        t('pages.pandadocProvisioning.toasts.syncError', {
          error: status.lastError,
        }),
      );
      stopPolling();
      return;
    }

    // `detailsPending === 0` alone is not "done": right after the trigger the
    // backend hasn't marked anything for backfill yet, and the reparse scans
    // the whole inventory before it does. `syncRunning` (the backend's own
    // run lock) is the authority — while it is held, a run is genuinely in
    // flight. The `responses > 1` guard still covers the gap between the
    // mutation returning and the worker picking the task up, and
    // `runStarted` keeps a run that never started at all — dead worker, queue
    // backed up — from being reported as a success just because the backlog
    // happens to be empty.
    if (
      runStartedRef.current &&
      pending === 0 &&
      !status.syncRunning &&
      responsesRef.current > 1
    ) {
      notify(
        ToastTypes.success,
        reparsing
          ? t('pages.pandadocProvisioning.toasts.reparseDone')
          : t('pages.pandadocProvisioning.toasts.syncDone'),
      );
      stopPolling();
      onSyncedRef.current();
    }
  }, [data, networkStatus, syncing, reparsing, notify, stopPolling, t]);

  return { triggerSync, triggerReparse, syncing, reparsing, detailsPending };
};
